import { Box, Grid } from "@material-ui/core"
import Map from "../../components/map/Map"
import NCText from "../../shared/NCText"
import { AddIcon, DriverEditIcon, DriverLocationIcon, DropLocationIcon, PickupLocationIcon } from "../../libs/icons"
import NCSvgIcon from "../../shared/NCSvgIcon"
import { Form, FormikProvider, useFormik } from "formik"
import FormField from "../../components/FormField"
import FormSection from "../../components/FormSection"
import { formatAMPM, formatDate, getAddressValue } from "../../libs/utils"
import NCButton from "../../shared/NCButton"
import NCInput from "../../shared/NCInput"
import NCModal from "../../shared/NCModal"
import * as Yup from "yup"
import { useState } from "react"
import { useMutation, useQueryClient } from "react-query"
import API from "../../libs/api"
import { API_PATH } from "../../libs/config"
import { toast } from "react-toastify"

const Leg = ({ currentRideDetails, showAddChangeModal, showAddChangeAidMonitorModal }) => {
    const queryClient = useQueryClient()
    const [showCancelModal, setShowCancelModal] = useState(false)
    const [cancelReason, setCancelReason] = useState("")

    const cancelTrip = useMutation(
        () => API.post(`${API_PATH.TRIPS}${currentRideDetails?.id}/admin-cancel/`, { reason: cancelReason }),
        {
            onSuccess: () => {
                toast.success("Trip cancelled")
                queryClient.invalidateQueries(["getRideDetails"])
                setShowCancelModal(false)
            }
        }
    )
    const startAddress = getAddressValue(currentRideDetails?.pickup_address?.address)
    const dropAddress = getAddressValue(currentRideDetails?.drop_off_address?.address)
    const center = currentRideDetails?.location_data?.driver?.latitude
        ? { lat: currentRideDetails.location_data.driver.latitude, lng: currentRideDetails.location_data.driver.longitude }
        : currentRideDetails?.pickup_address?.latitude
            ? { lat: currentRideDetails.pickup_address.latitude, lng: currentRideDetails.pickup_address.longitude }
            : { lat: 26.76628, lng: 83.36889 }

    const driverLocation = currentRideDetails?.location_data?.driver?.latitude
        ? { lat: currentRideDetails.location_data.driver.latitude, lng: currentRideDetails.location_data.driver.longitude }
        : null

    // B-leg (school → home): the relevant "arrival" time is the school pickup, not the drop-off.
    const isBLeg = currentRideDetails?.request_data?.type === 2

    const formik = useFormik({
        initialValues: {},
        validationSchema: Yup.object().shape({})
    })

    return (
        <>
            <Grid container>
                <Grid item xs={12} style={{ display: "flex" }}>
                    <Grid xs={6} style={{ padding: "10px" }}>
                        <Map startAddress={startAddress} dropAddress={dropAddress} center={center} currentRideDetails={currentRideDetails} driverLocation={driverLocation} />
                    </Grid>
                    <Grid xs={6}>
                        <Box border="1px solid #E3E3E3" borderRadius={8} m={1} bgcolor="#fff">
                            <Box display="flex" bgcolor="#F2F2F2" borderRadius="8px 8px 0 0" p="8px 16px">
                                <NCText variant="subtitle2"><Box fontWeight="bold">Ride Details</Box></NCText>
                            </Box>
                            <Box p={2}>
                                <FormikProvider value={formik}>
                                    <Form>
                                        <Box mb={2}>
                                            <NCText variant="body2" style={{ color: "#6B7280", marginBottom: 4 }}>Date *</NCText>
                                            <FormField style={{ width: "100%" }} value={formatDate(currentRideDetails?.pickup_time, currentRideDetails?.timezone) || "—"} readOnly />
                                        </Box>
                                        {/* Stops/route only exist once a driver is assigned (RideGroup). Before that the
                                            "estimated time of arrival" is the provisional school bell time
                                            (start bell for A-leg, end bell for B-leg). */}
                                        <Box mb={2}>
                                            <NCText variant="body2" style={{ color: "#6B7280", marginBottom: 4 }}>The estimated time of arrival * </NCText>
                                            <FormField style={{ width: "100%" }}
                                                value={currentRideDetails?.ride_group_id
                                                    ? formatAMPM(
                                                        isBLeg
                                                            ? (currentRideDetails?.timing_details?.pickup_time || currentRideDetails?.pickup_time)
                                                            : (currentRideDetails?.timing_details?.dropped_time || currentRideDetails?.pickup_time),
                                                        currentRideDetails?.timezone
                                                    )
                                                    : (currentRideDetails?.bell_time
                                                        ? formatAMPM(currentRideDetails?.bell_time, currentRideDetails?.timezone)
                                                        : "—")}
                                                readOnly />
                                        </Box>
                                        <Box mb={2}>
                                            <NCText variant="body2" style={{ color: "#6B7280", marginBottom: 4 }}>Pick up address *</NCText>
                                            <FormField style={{ width: "100%" }} value={getAddressValue(currentRideDetails?.pickup_address?.address)} readOnly />
                                        </Box>
                                        <Box mb={2}>
                                            <NCText variant="body2" style={{ color: "#6B7280", marginBottom: 4 }}>Drop off address *</NCText>
                                            <FormField style={{ width: "100%" }} value={getAddressValue(currentRideDetails?.drop_off_address?.address)} readOnly />
                                        </Box>
                                    </Form>
                                </FormikProvider>
                            </Box>
                            <Box display="flex" justifyContent="space-between" bgcolor="#F2F2F2" borderRadius="0 0 8px 8px" p="8px 16px">
                                <NCText variant="body2" style={{ color: "#7D7D7D" }}>
                                    <NCSvgIcon component={PickupLocationIcon} style={{ marginRight: 4, top: 4 }} /> Pickup Location
                                </NCText>
                                <NCText variant="body2" style={{ color: "#7D7D7D" }}>
                                    <NCSvgIcon component={DropLocationIcon} style={{ marginRight: 4, top: 4 }} /> Drop Location
                                </NCText>
                                <NCText variant="body2" style={{ color: "#7D7D7D" }}>
                                    <NCSvgIcon component={DriverLocationIcon} style={{ marginRight: 5, top: 5 }} /> Driver Location
                                </NCText>
                            </Box>
                        </Box>
                    </Grid>
                </Grid>
            </Grid>

            <Grid container spacing={2} style={{ marginTop: 8, padding: "0 8px" }}>
                <Grid item xs={12}>
                    <FormSection
                        title={<span style={{ fontWeight: "500" }}>Driver Details</span>}
                        subTitle={
                            <NCButton color="primary" variant="contained"
                                endIcon={<NCSvgIcon component={currentRideDetails?.driver ? DriverEditIcon : AddIcon} inheritViewBox />}
                                onClick={() => showAddChangeModal(true)}>
                                {currentRideDetails?.driver ? "Change Driver" : "Add Driver"}
                            </NCButton>
                        }
                    >
                        {currentRideDetails?.driver ? (
                            <Grid container spacing={2}>
                                <Grid item xs={4}>
                                    <NCText block style={{ fontSize: 12, color: "#9CA3AF", fontWeight: 400 }}>Name</NCText>
                                    <NCText block style={{ fontSize: 14, fontWeight: 400 }}>{currentRideDetails.driver.name}</NCText>
                                </Grid>
                                <Grid item xs={4}>
                                    <NCText block style={{ fontSize: 12, color: "#9CA3AF", fontWeight: 400 }}>Email</NCText>
                                    <NCText block style={{ fontSize: 14, fontWeight: 400 }}>{currentRideDetails.driver.email}</NCText>
                                </Grid>
                                <Grid item xs={4}>
                                    <NCText block style={{ fontSize: 12, color: "#9CA3AF", fontWeight: 400 }}>Phone Number</NCText>
                                    <NCText block style={{ fontSize: 14, fontWeight: 400 }}>{currentRideDetails.driver.phone}</NCText>
                                </Grid>
                            </Grid>
                        ) : null}
                    </FormSection>
                </Grid>

                <Grid item xs={12}>
                    <FormSection
                        title={<span style={{ fontWeight: "500" }}>Aid/Monitor Details</span>}
                        subTitle={
                            <NCButton color="primary" variant="contained"
                                endIcon={<NCSvgIcon component={AddIcon} inheritViewBox />}
                                onClick={() => showAddChangeAidMonitorModal(true)}>
                                {currentRideDetails?.aid_monitor ? "Change" : "Add"}
                            </NCButton>
                        }
                    >
                        {currentRideDetails?.aid_monitor ? (
                            <Grid container spacing={2}>
                                <Grid item xs={4}>
                                    <NCText block style={{ fontSize: 12, color: "#9CA3AF", fontWeight: 400 }}>Name</NCText>
                                    <NCText block style={{ fontSize: 14, fontWeight: 400 }}>{currentRideDetails.aid_monitor.name || "—"}</NCText>
                                </Grid>
                                <Grid item xs={4}>
                                    <NCText block style={{ fontSize: 12, color: "#9CA3AF", fontWeight: 400 }}>Email</NCText>
                                    <NCText block style={{ fontSize: 14, fontWeight: 400 }}>{currentRideDetails.aid_monitor.email || "—"}</NCText>
                                </Grid>
                                <Grid item xs={4}>
                                    <NCText block style={{ fontSize: 12, color: "#9CA3AF", fontWeight: 400 }}>Phone</NCText>
                                    <NCText block style={{ fontSize: 14, fontWeight: 400 }}>{currentRideDetails.aid_monitor.phone || "—"}</NCText>
                                </Grid>
                            </Grid>
                        ) : null}
                    </FormSection>
                </Grid>

            </Grid>

            <Box display="flex" justifyContent="flex-end" width="100%" mt={2} px={1}>
                <NCButton
                    variant="contained"
                    style={{ backgroundColor: "#E22134", color: "#fff", width: "auto" }}
                    onClick={() => { setCancelReason(""); setShowCancelModal(true) }}
                >
                    Cancel Trip
                </NCButton>
            </Box>

            <NCModal
                open={showCancelModal}
                title="Cancel Trip"
                onClose={() => { setShowCancelModal(false); setCancelReason("") }}
                submitButton={
                    <NCButton
                        variant="contained"
                        color="secondary"
                        disableElevation
                        disabled={!cancelReason.trim() || cancelTrip.isLoading}
                        onClick={() => cancelTrip.mutate()}
                        style={{ width: "auto" }}
                    >
                        {cancelTrip.isLoading ? "Cancelling..." : "Cancel Trip"}
                    </NCButton>
                }
            >
                <Box mt={1} width="100%">
                    <NCText style={{ fontSize: 14, display: "block", marginBottom: 20 }}>
                        You are about to cancel trip <strong>#{currentRideDetails?.id}</strong>
                        {currentRideDetails?.pickup_time ? <> scheduled for <strong>{formatDate(currentRideDetails.pickup_time, currentRideDetails?.timezone)}</strong></> : ""}
                        This action cannot be undone. Are you sure you want to proceed?
                    </NCText>
                    <NCText style={{ fontWeight: 600, fontSize: 14, marginBottom: 8, display: "block" }}>Why cancel this trip?</NCText>
                    <NCInput
                        multiline
                        minRows={3}
                        maxRows={6}
                        fullWidth
                        placeholder="Enter reason for cancellation..."
                        value={cancelReason}
                        onChange={e => setCancelReason(e.target.value)}
                    />
                </Box>
            </NCModal>
        </>
    )
}

export default Leg
